import type { ActivityCockpitSnapshot } from '@internal/spassu-code-contract';
import type { Dispatch } from 'react';
import { useCallback, useRef } from 'react';
import type { CockpitViewModel } from '../model/cockpitViewModel';
import type { CockpitDataSource } from './CockpitDataSource';
import { asCockpitError } from './cockpitControllerErrors';
import type { CockpitControllerAction } from './cockpitControllerTypes';
import { CockpitRequestCoordinator } from './cockpitRequestCoordinator';

interface SnapshotLoaderContext {
  activityId: string;
  dataSource: CockpitDataSource;
  adaptSnapshot(snapshot: ActivityCockpitSnapshot): CockpitViewModel;
  staleAfterMs: number;
  dispatch: Dispatch<CockpitControllerAction>;
  requests: CockpitRequestCoordinator;
}

export function useCockpitSnapshotLoader(
  context: Omit<SnapshotLoaderContext, 'requests'>,
) {
  const requests = useRef(new CockpitRequestCoordinator()).current;
  const { activityId, dataSource, adaptSnapshot, staleAfterMs, dispatch } =
    context;
  const loadSnapshot = useCallback(
    (revision?: number, polling = false) =>
      requestSnapshot(
        {
          activityId,
          dataSource,
          adaptSnapshot,
          staleAfterMs,
          dispatch,
          requests,
        },
        revision,
        polling,
      ),
    [activityId, adaptSnapshot, dataSource, dispatch, requests, staleAfterMs],
  );
  return { loadSnapshot, requests };
}

async function requestSnapshot(
  context: SnapshotLoaderContext,
  revision?: number,
  polling = false,
): Promise<void> {
  if (polling && context.requests.hasActive()) return;
  const request = context.requests.begin();
  context.dispatch({ type: 'snapshotStarted', activityId: context.activityId });
  try {
    const snapshot = await context.dataSource.getSnapshot({
      activityId: context.activityId,
      revision,
      signal: request.signal,
    });
    validateActivity(snapshot.activityId, context.activityId, 'snapshot');
    const model = context.adaptSnapshot(snapshot);
    if (!context.requests.succeed(request, Date.now())) return;
    context.dispatch({ type: 'snapshotSucceeded', snapshot, model });
  } catch (reason) {
    if (!context.requests.isCurrent(request)) return;
    context.requests.finish(request);
    context.dispatch({
      type: 'snapshotFailed',
      error: asCockpitError(reason),
      stale: context.requests.isStale(Date.now(), context.staleAfterMs),
    });
  }
}

export function validateActivity(
  actual: string,
  expected: string,
  resource: string,
): void {
  if (actual !== expected) {
    throw new Error(
      `Cockpit ${resource} does not match the requested activity.`,
    );
  }
}
